import { useQueryClient } from "@tanstack/react-query";
import { globalAppAndLanguageDefaultState } from "../utils/constants/GlobalAppAndLanguage";
import useAmsConfig from "./useAmsConfig";
import useZustandStore from "../zustandStore/useZustandStore";

/** Selected app + language, their labels, and switching that refetches every page. */
const useGlobalAppAndLanguage = () => {
  const globalAppAndLanguage =
    useZustandStore((s) => s.globalAppAndLanguage) ||
    globalAppAndLanguageDefaultState;
  const setGlobalAppAndLanguage = useZustandStore(
    (s) => s.setGlobalAppAndLanguage
  );
  const setAppSwitch = useZustandStore((s) => s.setAppSwitch);
  const lastLanguageByApp = useZustandStore((s) => s.lastLanguageByApp);
  const queryClient = useQueryClient();
  const { appNames, languageMapping, languagesFor } = useAmsConfig();
  const { app, language } = globalAppAndLanguage;
  const appLabel = appNames.find((a) => a.value === app)?.label || app;
  const languageLabel = languageMapping[language] || language;

  const handleResolveLanguage = ({ app: nextApp, language: wanted }) => {
    const languages = languagesFor(nextApp).map((l) => l.value);
    return languages.includes(wanted) ? wanted : languages[0] || wanted;
  };

  const handleGlobalLabel = ({ app: forApp, language: forLanguage }) =>
    `${appNames.find((a) => a.value === forApp)?.label || forApp} · ${
      languageMapping[forLanguage] || forLanguage
    }`;

  const handleSwitchGlobalApp = ({ app: nextApp, language: nextLanguage }) => {
    const target = (v) => (v || "").trim().toLowerCase();
    const valid = languagesFor(target(nextApp)).some(
      (l) => l.value === target(nextLanguage)
    );
    if (!valid) return false;
    if (target(nextApp) === app && target(nextLanguage) === language)
      return false;
    const to = { app: target(nextApp), language: target(nextLanguage) };
    setAppSwitch({ from: { app, language }, to });
    setGlobalAppAndLanguage(to);
    queryClient.resetQueries();
    return true;
  };

  return {
    globalApp: app,
    globalLanguage: language,
    appLabel,
    languageLabel,
    globalLabel: `${appLabel} · ${languageLabel}`,
    lastLanguageByApp: lastLanguageByApp || {},
    handleGlobalLabel,
    handleResolveLanguage,
    handleSwitchGlobalApp,
  };
};

export default useGlobalAppAndLanguage;
